import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mail, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TestEmailButton } from "./subscription/TestEmailButton";
import { TestWhatsAppButton } from "./subscription/TestWhatsAppButton";

export const SubscriptionForm = () => {
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [type, setType] = useState<"email" | "whatsapp">("email");
  const [isTesting, setIsTesting] = useState(false);
  const { toast } = useToast();

  const handleWhatsAppChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^\d+]/g, "");
    if (value.startsWith("+")) {
      value = "+" + value.substring(1).replace(/\+/g, "");
    }
    setWhatsapp(value);
  };

  const sendConfirmationEmail = async (email: string) => {
    try {
      await supabase.functions.invoke("send-sub-email", {
        body: {
          from: "Couple's Devotional <devotional@mail.hwdevotional.com>",
          replyTo: "devotional@mail.hwdevotional.com",
          to: [email],
          subject: "Welcome to Couple's Devotional!",
          html: `
            <h1>Subscription Confirmed!</h1>
            <p>Thank you for subscribing to our daily devotionals.</p>
            <p>May God bless your marriage and spiritual journey!</p>
          `,
        },
      });
    } catch (error) {
      console.error("Failed to send confirmation email:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const contact = type === "email" ? email : whatsapp;
      
      // Check if already subscribed
      const { data, error: selectError } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("type", type)
        .eq("contact", contact);

      if (selectError) throw selectError;

      if (data?.length > 0) {
        toast({
          title: "Already subscribed",
          description: `This ${type} is already subscribed.`,
          variant: "destructive",
        });
        return;
      }

      // Use SDK to insert subscription - verified starts as false
      const { error: insertError } = await supabase
        .from("subscriptions")
        .insert({ 
          type, 
          contact, 
          verified: false // User needs to verify their subscription
        });

      if (insertError) throw insertError;

      if (type === "email") await sendConfirmationEmail(email);

      toast({ 
        title: "Subscription successful!", 
        description: `You'll receive devotionals via ${type}. Please check your ${type === "email" ? "inbox" : "messages"} for confirmation.` 
      });

      setEmail("");
      setWhatsapp("");
      
    } catch (error: any) {
      console.error("Error subscribing:", error);
      toast({ title: "Subscription failed", description: error.message, variant: "destructive" });
    }
  };

  return (
    <div className="w-full max-w-md mx-auto space-y-4">
      <div className="flex space-x-2 mb-4">
        <Button variant={type === "email" ? "default" : "outline"} onClick={() => setType("email")} className="flex-1 gap-2">
          <Mail className="h-4 w-4" /> Email
        </Button>
        <Button variant={type === "whatsapp" ? "default" : "outline"} onClick={() => setType("whatsapp")} className="flex-1 gap-2">
          <MessageSquare className="h-4 w-4" /> WhatsApp
        </Button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {type === "email" ? (
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        ) : (
          <Input
            type="tel"
            placeholder="Enter your WhatsApp number"
            value={whatsapp}
            onChange={handleWhatsAppChange}
            pattern="^\+?[1-9]\d{1,14}$"
            required
          />
        )}
        {(type === "email" ? email : whatsapp) && (
          type === "email" ? (
            <TestEmailButton email={email} disabled={isTesting} />
          ) : (
            <TestWhatsAppButton whatsapp={whatsapp} disabled={isTesting} />
          )
        )}
        <Button type="submit" className="w-full">Subscribe</Button>
      </form>
    </div>
  );
};